/**
 * Parametry WSPÓLNYCH źródeł ciepła budynku w panelu: podgląd (prompt 07 pkt 1, 2026-10-10) i edycja
 * lokalizacji, notatki i parametrów (B8, 2026-10-19 — HeatSourceEditModal) z `heatSources[].params`
 * (GET /buildings/:id).
 *
 * LUSTRO pól formularza źródła z mobile/src/constants/heatSourceSchema.ts (HEAT_SOURCE_SCHEMA +
 * BUILDING_SOURCE_NOTE_FIELD) — etykiety i opcje 1:1 z tabletu, w tym samym kształcie literałów.
 * Bez „Charakteru" (panel pokazuje skrót z ds/HeatSourceLabels) i bez pól „Lokalizacji" (słownik
 * pomieszczeń = PgCogDetails.ROOM_OPTIONS). Parytet: backend/test/heatSourceParamsParity.test.js.
 */
import { ROOM_OPTIONS } from '../components/RecommendationDetails/PgCogDetails';

export type ParamFieldType = 'select' | 'text' | 'number' | 'checkbox';

export interface ParamField {
  key: string;
  label: string;
  type: ParamFieldType;
  options?: { value: string; label: string }[];
  /** Pole ma sens tylko przy tej wartości innego pola (np. pojemność tylko przy zbiorniku). */
  showIf?: { key: string; equals: string | boolean | number };
}

const PALIWO_GAZ_OPTIONS = [
  { value: 'gaz_przewodowy', label: 'Gaz przewodowy' },
  { value: 'gaz_butla', label: 'Gaz butla, zbiornik LPG lub LNG' },
];

const KLASA_KOTLA_OPTIONS = [
  { value: '5', label: 'Klasa 5' },
  { value: '4', label: 'Klasa 4' },
  { value: '3', label: 'Klasa 3' },
  { value: 'brak', label: 'Bez klasy / poniżej klasy 3' },
];

const SPOSOB_PODAWANIA_OPTIONS = [
  { value: 'automatyczny', label: 'Automatyczny (podajnik)' },
  { value: 'reczny_bez_wentylatora', label: 'Ręczny bez wentylatora' },
  { value: 'reczny_z_wentylatorem', label: 'Ręczny z wentylatorem' },
];

const PALIWO_OLEJ_OPTIONS = [
  { value: 'olej_opalowy', label: 'Olej opałowy' },
];

const PALIWO_STALE_OPTIONS = [
  { value: 'orzech', label: 'Węgiel - orzech' },
  { value: 'wegiel_kostka', label: 'Węgiel - kostka' },
  { value: 'wegiel_groszek', label: 'Węgiel - groszek / ekogroszek' },
  { value: 'pellet', label: 'Pellet' },
  { value: 'drewno', label: 'Drewno kawałkowe' },
  { value: 'biomasa', label: 'Biomasa pozostała' },
];

const PALIWO_BIOMASA_OPTIONS = [
  { value: 'pellet', label: 'Pellet' },
  { value: 'drewno', label: 'Drewno kawałkowe' },
  { value: 'biomasa', label: 'Biomasa pozostała' },
];

const STANDARDY_NISKOEMISYJNE_OPTIONS = [
  { value: 'inne', label: 'Inne standardy niskoemisyjne' },
  { value: 'nie', label: 'Nie' },
  { value: 'ekoprojekt', label: 'Ekoprojekt' },
];

const PALIWO_KAFLOWY_OPTIONS = [
  { value: 'wegiel_kostka', label: 'Węgiel kostka' },
  { value: 'orzech', label: 'Węgiel orzech' },
  { value: 'wegiel_groszek', label: 'Węgiel groszek' },
  { value: 'pellet', label: 'Pellet / brykiet drzewny' },
];

const PALIWO_PIECOKUCHNIA_OPTIONS = [
  { value: 'biomasa', label: 'Inna biomasa' },
  { value: 'drewno', label: 'Drewno kawałkowe' },
  { value: 'pellet', label: 'Pellet / brykiet drzewny' },
  { value: 'wegiel_kostka', label: 'Węgiel kostka' },
  { value: 'wegiel_groszek', label: 'Węgiel groszek' },
];

const PALIWO_TRZON_OPTIONS = [
  { value: 'wegiel_kostka', label: 'Węgiel kostka' },
  { value: 'orzech', label: 'Węgiel orzech' },
  { value: 'wegiel_groszek', label: 'Węgiel groszek' },
  { value: 'wegiel_mial', label: 'Węgiel miał' },
  { value: 'wegiel_brunatny', label: 'Węgiel brunatny' },
  { value: 'pellet', label: 'Pellet / brykiet drzewny' },
  { value: 'biomasa', label: 'Inna biomasa' },
  { value: 'drewno', label: 'Drewno kawałkowe' },
];

const ZRODLO_DANYCH_KAFLOWY_OPTIONS = [
  { value: 'inne', label: 'Inne' },
  { value: 'dokumentacja', label: 'Z dokumentacji techniczno-ruchowej' },
];

const EKOPROJEKT_OPTIONS = [
  { value: 'tak', label: 'TAK' },
  { value: 'nie', label: 'NIE' },
  { value: 'inne', label: 'INNE' },
];

const ZRODLO_DANYCH_KOMINEK_OPTIONS = [
  { value: 'tabliczka', label: 'Z tabliczki znamionowej' },
  { value: 'dokumentacja', label: 'Z dokumentacji techniczno-ruchowej' },
  { value: 'inne', label: 'Inne' },
];

const TYP_POMPY_OPTIONS = [
  { value: 'powietrzna', label: 'Powietrzna' },
  { value: 'gruntowa', label: 'Gruntowa' },
  { value: 'wodna', label: 'Wodna' },
];

const ZRODLO_DANYCH_OPTIONS = [
  { value: 'inne', label: 'Inne' },
  { value: 'tabliczka', label: 'Z tabliczki znamionowej' },
  { value: 'dokumentacja', label: 'Z dokumentacji techniczno-ruchowej' },
];

/** Pola parametrów per typ, w kolejności formularza tabletu. */
const HEAT_SOURCE_PARAM_FIELDS: Record<string, ParamField[]> = {
  gas_boiler: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_GAZ_OPTIONS },
  ],
  solid_fuel_boiler: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'klasa', label: 'Klasa kotła', type: 'select', options: KLASA_KOTLA_OPTIONS },
    { key: 'ekoprojekt', label: 'Ekoprojekt', type: 'checkbox' },
    { key: 'sposobPodawania', label: 'Sposób podawania paliwa', type: 'select', options: SPOSOB_PODAWANIA_OPTIONS },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'nieekoemisyjny', label: 'Nieekoemisyjny', type: 'checkbox' },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_STALE_OPTIONS },
    { key: 'zrodloDanych', label: 'Źródło danych', type: 'select', options: ZRODLO_DANYCH_OPTIONS },
  ],
  oil_boiler: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_OLEJ_OPTIONS },
  ],
  solar_collectors: [
    { key: 'liczbaPaneli', label: 'Liczba paneli', type: 'text' },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
  ],
  fireplace: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_BIOMASA_OPTIONS },
    { key: 'ekoprojekt', label: 'Ekoprojekt', type: 'select', options: EKOPROJEKT_OPTIONS },
    { key: 'zrodloDanychKominek', label: 'Źródło danych', type: 'select', options: ZRODLO_DANYCH_KOMINEK_OPTIONS },
  ],
  electric_heating: [
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
  ],
  tile_stove: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'urzadzenieOdpylajace', label: 'Urządzenie odpylające', type: 'checkbox' },
    { key: 'standardyNiskoemisyjne', label: 'Standardy niskoemisyjne', type: 'select', options: STANDARDY_NISKOEMISYJNE_OPTIONS },
    { key: 'zrodloDanych', label: 'Źródło danych', type: 'select', options: ZRODLO_DANYCH_KAFLOWY_OPTIONS },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_KAFLOWY_OPTIONS },
  ],
  kitchen_stove: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'urzadzenieOdpylajace', label: 'Urządzenie odpylające', type: 'checkbox' },
    { key: 'standardyNiskoemisyjne', label: 'Standardy niskoemisyjne', type: 'select', options: STANDARDY_NISKOEMISYJNE_OPTIONS },
    { key: 'zrodloDanych', label: 'Źródło danych', type: 'select', options: ZRODLO_DANYCH_OPTIONS },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_PIECOKUCHNIA_OPTIONS },
  ],
  heat_pump: [
    { key: 'typPompy', label: 'Rodzaj pompy ciepła', type: 'select', options: TYP_POMPY_OPTIONS },
    { key: 'bufor', label: 'Bufor ciepła lub ciepłej wody użytkowej', type: 'checkbox' },
    { key: 'zbiornik', label: 'Zbiornik akumulacyjny', type: 'checkbox' },
    { key: 'pojemnoscZbiornika', label: 'Pojemność zbiornika akumulacyjnego [m³]', type: 'number', showIf: { key: 'zbiornik', equals: true } },
  ],
  kitchen_core: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'planowanaWymiana', label: 'Planowana wymiana', type: 'checkbox' },
    { key: 'urzadzenieOdpylajace', label: 'Urządzenie odpylające', type: 'checkbox' },
    { key: 'standardyNiskoemisyjne', label: 'Standardy niskoemisyjne', type: 'select', options: STANDARDY_NISKOEMISYJNE_OPTIONS },
    { key: 'zrodloDanych', label: 'Źródło danych', type: 'select', options: ZRODLO_DANYCH_OPTIONS },
    { key: 'paliwo', label: 'Rodzaj paliwa', type: 'select', options: PALIWO_TRZON_OPTIONS },
  ],
  network: [],
  photovoltaic: [
    { key: 'yearInstalled', label: 'Rok montażu', type: 'number' },
    { key: 'liczbaPaneli', label: 'Liczba paneli', type: 'text' },
  ],
};

/** Etykieta pola „Lokalizacja" (każdy typ) i notatki wspólnego źródła — z formularza tabletu. */
export const LOCATION_FIELD_LABEL = 'Lokalizacja (pomieszczenie)';
export const NOTE_FIELD_LABEL = 'Notatka';
export const NOTE_FIELD_HINT = 'Np. gdzie jest źródło, kto ma klucze — tylko dla nas, nie idzie do CEEB';
/** Opcja „Inne (wpisz)" słownika pomieszczeń i pole ręcznego wpisu (`lokalizacjaInna`) — z tabletu. */
export const LOCATION_OTHER_VALUE = 'inne';
export const LOCATION_OTHER_OPTION_LABEL = 'Inne (wpisz)';
export const LOCATION_OTHER_FIELD_LABEL = 'Inna lokalizacja';

/** Pola parametrów typu w kolejności formularza tabletu (bez charakteru i lokalizacji); nieznany typ → []. */
export function heatSourceParamFields(type: string): ParamField[] {
  return HEAT_SOURCE_PARAM_FIELDS[type] || [];
}

/** Pole widoczne przy tych wartościach (warunek `showIf`, np. pojemność tylko przy zbiorniku). */
export function isParamFieldVisible(field: ParamField, values: Record<string, unknown>): boolean {
  return !field.showIf || values[field.showIf.key] === field.showIf.equals;
}

/** Najstarszy przyjmowany „Rok montażu" — LUSTRO MIN_INSTALL_YEAR z heatSourceSchema.ts. */
export const MIN_INSTALL_YEAR = 1900;

/** LUSTRO isValidInstallYear tabletu: dokładnie 4 cyfry, od MIN_INSTALL_YEAR do bieżącego roku. */
export function isValidInstallYear(value: unknown, now: Date = new Date()): boolean {
  const text = String(value ?? '').trim();
  if (!/^\d{4}$/.test(text)) return false;
  const year = Number(text);
  return year >= MIN_INSTALL_YEAR && year <= now.getFullYear();
}

/** LUSTRO installYearError tabletu — null, gdy pole puste („Brak danych") albo rok poprawny. */
export function installYearError(value: unknown, now: Date = new Date()): string | null {
  const text = String(value ?? '').trim();
  if (!text || isValidInstallYear(text, now)) return null;
  return `Niepoprawny rok — wpisz 4 cyfry (${MIN_INSTALL_YEAR}–${now.getFullYear()}). Bez poprawki do CEEB pójdzie „Brak danych".`;
}

/**
 * Zmiany parametrów do zapisu: tylko pola, które admin ZMIENIŁ względem wartości z odczytu, plus reguła
 * tabletu (applyFieldRules) dla „Roku montażu": poprawny rok = znana wartość (`yearInstalledUnknown`
 * false → bot wpisuje rok w CEEB), puste/błędne = „Brak danych" (true). Pusty wynik = nic do zapisu.
 */
export function heatParamChanges(
  original: Record<string, unknown>,
  edited: Record<string, unknown>,
  now: Date = new Date(),
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(edited)) {
    if (value !== original[key]) changes[key] = value;
  }
  if ('yearInstalled' in changes) {
    const unknown = !isValidInstallYear(changes.yearInstalled, now);
    if (original.yearInstalledUnknown !== unknown) changes.yearInstalledUnknown = unknown;
  }
  return changes;
}

export interface HeatSourceParamRow {
  key: string;
  label: string;
  value: string;
}

const isEmpty = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

function formatValue(field: ParamField, value: unknown): string {
  if (field.type === 'checkbox' && typeof value === 'boolean') return value ? 'Tak' : 'Nie';
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === String(value));
    // Wartość spoza słownika (np. surowy tekst z importu CEEB) — pokazujemy dosłownie.
    if (option) return option.label;
  }
  if (field.type === 'number' && typeof value === 'number') return String(value).replace('.', ',');
  return String(value).trim();
}

/**
 * Zapisane parametry źródła w kolejności formularza tabletu, z polskimi etykietami.
 * Pomija puste pola i pola ukryte warunkiem (np. pojemność bez zbiornika); nieznany typ → [].
 */
export function heatSourceParamRows(type: string, params?: Record<string, unknown> | null): HeatSourceParamRow[] {
  const fields = HEAT_SOURCE_PARAM_FIELDS[type] || [];
  const p = params || {};
  return fields
    .filter((f) => !isEmpty(p[f.key]))
    .filter((f) => !f.showIf || p[f.showIf.key] === f.showIf.equals)
    .map((f) => ({ key: f.key, label: f.label, value: formatValue(f, p[f.key]) }));
}

/**
 * Pomieszczenie źródła („Lokalizacja"): etykieta ze słownika, „Inne" → ręczny wpis (bez wpisu: „Inne"),
 * wartość spoza słownika dosłownie, brak wyboru → null. Reguła jak w PDF podsumowania
 * (backend constants/heatSourceLabels.js heatSourceLocationLabel).
 */
export function heatSourceLocationLabel(params?: Record<string, unknown> | null): string | null {
  const value = typeof params?.lokalizacja === 'string' ? params.lokalizacja.trim() : '';
  if (!value) return null;
  if (value === 'inne') {
    const other = typeof params?.lokalizacjaInna === 'string' ? params.lokalizacjaInna.trim() : '';
    return other || 'Inne';
  }
  return ROOM_OPTIONS.find((r) => r.id === value)?.label ?? value;
}

/** Notatka wspólnego źródła (params.notatka) albo null. */
export function heatSourceNote(params?: Record<string, unknown> | null): string | null {
  const note = typeof params?.notatka === 'string' ? params.notatka.trim() : '';
  return note || null;
}
